<template>
  <RevLoadingScreen v-if="pending" :text="i18n(translations.fetchingData)" />
  <div v-else>
    <AddComment
      :listing-id="listing.legacyId"
      @comment-submitted="handleCommentSubmitted"
    />

    <div v-if="missingMarkets.length" class="mb-16 flex items-center">
      <IconWarning class="text-static-danger-hi mr-2" />

      <span class="body-1">
        {{ i18n(translations.missingMarkets) }}
      </span>

      <ul class="ml-4 flex max-w-[36rem] list-none flex-wrap gap-8">
        <li
          v-for="market in missingMarkets"
          :key="market"
          class="border-static-danger-hi body-2 flex items-center justify-between border px-6 py-2"
        >
          <Flag :country-code="market as MarketCountryCode" />

          <span class="text-static-danger-hi body-2-bold ml-1">
            {{ market }}
          </span>
        </li>
      </ul>
    </div>

    <div v-if="comments.length">
      <RevTable :collection="comments" :columns transparent-header>
        <template #body-market="{ item }">
          <div class="body-2 flex items-center justify-between px-6 py-2">
            <Flag
              class="mr-8"
              :country-code="item.market as MarketCountryCode"
              size="m"
            />
            {{ i18n.country(item.market) }}
          </div>
        </template>
        <template #body-level="{ item }">
          <RevTag :label="item.level.label" variant="secondary" />
        </template>

        <template #body-comment="{ item }">
          <div class="flex w-full items-center">
            <div class="flex-1">{{ item.comment }}</div>

            <RevButton
              v-if="item.level.code === 'LISTING'"
              size="small"
              variant="secondary"
              @click="handleDeleteComment(item.market)"
            >
              {{ i18n(translations.delete) }}
            </RevButton>
          </div>
        </template>
      </RevTable>
    </div>

    <div v-else>
      {{ i18n(translations.noResults) }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import { merchantsAPI } from '@backmarket/http-api'
import { MarketCountryCode } from '@backmarket/http-api/src/api-models/MarketCountryCode'
import type { Listing } from '@backmarket/http-api/src/api-specs-seller-backoffice/merchants/listings.types'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useHttpFetch } from '@backmarket/nuxt-module-http/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { RevTable } from '@ds/components/Table'
import { RevTag } from '@ds/components/Tag'
import { IconWarning } from '@ds/icons/IconWarning'

import translations from './Comments.translations'
import AddComment from './components/AddComment.vue'

const i18n = useI18n()

const props = defineProps<{
  listing: Listing
}>()

const { data, pending, refresh } = useHttpFetch(
  merchantsAPI.getListingComments,
  {
    pathParams: {
      listingId: props.listing.legacyId,
    },
  },
)

const columns = [
  { key: 'market', label: i18n(translations.market) },
  { key: 'level', label: i18n(translations.commentLevel) },
  { key: 'comment', label: i18n(translations.comments) },
]

const comments = computed(() => {
  return data.value?.comments || []
})

const missingMarkets = computed(() => {
  return data.value?.missingMarkets || []
})

async function handleDeleteComment(market: string) {
  await $httpFetch(merchantsAPI.deleteListingComment, {
    pathParams: {
      listingId: props.listing.legacyId,
      market,
    },
  })
  refresh()
}

function handleCommentSubmitted() {
  refresh()
}
</script>
